import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { ArrowSimple } from '@stokr/components-library'
import { Menu, MenuButton, MenuItem } from './OptionMenu.styles'

interface OptionMenuProps {
  label: ReactNode
  /** The menu's accessible name. */
  menuLabel: string
  options: { value: string; label: string }[]
  selected?: string
  /** The trigger reads as chosen (outlined). */
  active: boolean
  onSelect: (value: string) => void
  /** The side the list lines up with: `end` for a trigger at the right edge. */
  align?: 'start' | 'end'
  disabled?: boolean
  /** Test-automation hook: the button gets `data-cy="<dataCy>"`, each option `<dataCy>-option-<value>`. */
  dataCy?: string
}

/** Space between the button and its list, and kept from the window's bottom edge, px. */
const GAP = 4
const EDGE = 8
const MAX_HEIGHT = 280

/**
 * A button opening a list to pick one from (Figma "HISTORICAL ⌄": NAV months, payment currencies).
 * The list goes to <body>, fixed under the button: the cards it sits in clip their content. It
 * closes on a pick, a press outside, Escape, a scroll or a resize; focus goes back to the button.
 */
export const OptionMenu = ({
  label,
  menuLabel,
  options,
  selected,
  active,
  onSelect,
  align = 'start',
  disabled,
  dataCy,
}: OptionMenuProps) => {
  const [open, setOpen] = useState(false)
  const [place, setPlace] = useState<CSSProperties>()
  const anchorRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLUListElement>(null)
  const menuId = useId()

  // Placed before paint, from the button's box, then kept under it every frame: the button can
  // move with no scroll or resize (the mobile sheet shrinking once the keyboard has closed).
  useLayoutEffect(() => {
    const button = buttonRef.current
    if (!open || !button) return undefined
    let last = ''
    let frame = 0
    const follow = () => {
      const box = button.getBoundingClientRect()
      const key = `${box.bottom}|${box.left}|${box.right}`
      if (key !== last) {
        last = key
        const top = box.bottom + GAP
        setPlace({
          top,
          maxHeight: Math.min(MAX_HEIGHT, Math.max(120, window.innerHeight - top - EDGE)),
          ...(align === 'end' ? { right: document.documentElement.clientWidth - box.right } : { left: box.left }),
        })
      }
      frame = requestAnimationFrame(follow)
    }
    follow()
    return () => cancelAnimationFrame(frame)
  }, [open, align])

  useEffect(() => {
    if (!open) return undefined
    const close = (event: Event) => {
      if (event instanceof KeyboardEvent) {
        if (event.key !== 'Escape') return
        buttonRef.current?.focus()
      } else if (event.type === 'pointerdown') {
        const target = event.target as Node
        if (anchorRef.current?.contains(target) || menuRef.current?.contains(target)) return
      } else if (event.type === 'scroll' && menuRef.current?.contains(event.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', close)
    // Fixed under the button: any scroll or resize would leave it behind.
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    // The chosen option, else the first, takes focus so the keyboard starts in the list.
    const menu = menuRef.current
    const target =
      menu?.querySelector<HTMLButtonElement>('[aria-checked="true"]') ??
      menu?.querySelector<HTMLButtonElement>('[role="menuitemradio"]')
    target?.focus({ preventScroll: true })
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', close)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  return (
    <div ref={anchorRef}>
      <MenuButton
        ref={buttonRef}
        type="button"
        black
        $active={active}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        disabled={disabled}
        aria-disabled={disabled}
        data-cy={dataCy}
        onClick={() => !disabled && setOpen(!open)}>
        {label}
        <ArrowSimple width={10} height={6} aria-hidden="true" />
      </MenuButton>
      {open &&
        createPortal(
          <Menu ref={menuRef} id={menuId} role="menu" aria-label={menuLabel} style={place}>
            {options.map((option) => (
              <li key={option.value} role="none">
                <MenuItem
                  type="button"
                  role="menuitemradio"
                  aria-checked={option.value === selected}
                  $selected={option.value === selected}
                  data-cy={dataCy && `${dataCy}-option-${option.value}`}
                  onClick={() => {
                    onSelect(option.value)
                    setOpen(false)
                    buttonRef.current?.focus()
                  }}>
                  {option.label}
                </MenuItem>
              </li>
            ))}
          </Menu>,
          document.body,
        )}
    </div>
  )
}
